import { useEffect, useState } from "react";
import ApplicationInfoForm from "Components/PatentDesign/ApplicationInfoForm";
import DetailSection from "Components/PatentDesign/DetailSection";
import { disabledStyle, pdStyles } from "Components/PatentDesign/styles";
import Badge from "Components/UI/Badge";
import Button from "Components/UI/Button";
import Select from "Components/UI/Select";
import { updatePatentDesignRequestStatus } from "JS/API";
import { designTokens } from "utils/designSystem";
import {
  ERROR_MESSAGE,
  HISTORY_ACTOR,
  MAIL_NOTIFY_STATUSES,
  MANUAL_STATUSES,
  PATENT_DESIGN_STATUS,
  PATENT_DESIGN_STATUS_ORDER,
  PATENT_DESIGN_STATUS_STYLE,
  formatDateTime,
} from "utils/patentDesign";

const statusLabel = (status) => PATENT_DESIGN_STATUS[status] || status;

const actorText = (item) => {
  if (item.actor_type === "admin") {
    return [HISTORY_ACTOR.admin, item.admin_name].filter(Boolean).join(" · ");
  }
  return HISTORY_ACTOR[item.actor_type] || item.actor_type || "-";
};

const sortNewestFirst = (history = []) =>
  [...history].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

const ProgressSection = ({ detail, onReload, onAlert, onError }) => {
  const [nextStatus, setNextStatus] = useState(detail.status);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setNextStatus(detail.status);
  }, [detail.status]);

  const history = sortNewestFirst(detail.history);
  const isButtonDisabled = isUpdating || nextStatus === detail.status;

  const handleChange = async () => {
    if (isButtonDisabled) return;
    if (
      MAIL_NOTIFY_STATUSES.includes(nextStatus) &&
      !window.confirm(
        `'${statusLabel(nextStatus)}'(으)로 바꾸면 고객에게 메일이 발송됩니다. 변경할까요?`
      )
    ) {
      return;
    }
    setIsUpdating(true);
    try {
      const result = await updatePatentDesignRequestStatus(detail.id, nextStatus);
      if (result && typeof result === "object") {
        onAlert("진행 단계를 변경했습니다.");
        onReload();
      } else if (ERROR_MESSAGE[result]) {
        onAlert(ERROR_MESSAGE[result]);
      } else {
        onError(result);
      }
    } catch {
      onError();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <DetailSection
      title="진행 단계"
      actions={
        <Badge style={PATENT_DESIGN_STATUS_STYLE[detail.status]}>{statusLabel(detail.status)}</Badge>
      }
    >
      <label htmlFor="patent-design-next-status" style={pdStyles.label}>
        변경할 단계
      </label>
      <div style={pdStyles.row}>
        <Select
          id="patent-design-next-status"
          value={nextStatus}
          onChange={(e) => setNextStatus(e.target.value)}
          style={{ width: "220px" }}
        >
          {PATENT_DESIGN_STATUS_ORDER.map((status) => (
            <option key={status} value={status} disabled={!MANUAL_STATUSES.includes(status)}>
              {PATENT_DESIGN_STATUS[status]}
            </option>
          ))}
        </Select>
        <Button
          variant="primary"
          onClick={handleChange}
          disabled={isButtonDisabled}
          style={{ whiteSpace: "nowrap", ...disabledStyle(isButtonDisabled) }}
        >
          단계 변경
        </Button>
      </div>
      <p style={{ ...pdStyles.hint, marginTop: designTokens.spacing.xs }}>
        견적 발송 · 결제 완료는 자동으로 바뀌어요. 출원 완료 · 등록 완료 · 거절로 바꾸면 고객에게 메일이 발송돼요.
      </p>

      <h3 style={pdStyles.subTitle}>변경 이력</h3>
      {history.length ? (
        <div style={pdStyles.tableWrapper}>
          <table style={pdStyles.table}>
            <thead>
              <tr>
                <th style={pdStyles.th}>일시</th>
                <th style={pdStyles.th}>변경</th>
                <th style={pdStyles.th}>처리자</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item, index) => (
                <tr key={`${item.created_at}-${index}`}>
                  <td style={{ ...pdStyles.td, ...pdStyles.nowrap }}>
                    {formatDateTime(item.created_at)}
                  </td>
                  <td style={pdStyles.td}>
                    {item.from_status
                      ? `${statusLabel(item.from_status)} → ${statusLabel(item.to_status)}`
                      : statusLabel(item.to_status)}
                  </td>
                  <td style={pdStyles.td}>{actorText(item)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={pdStyles.muted}>이력 없음</p>
      )}

      <h3 style={pdStyles.subTitle}>출원 정보</h3>
      <ApplicationInfoForm
        requestId={detail.id}
        application={detail.application}
        onReload={onReload}
        onAlert={onAlert}
        onError={onError}
      />
    </DetailSection>
  );
};

export default ProgressSection;
